import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Download, Briefcase, Users, UserPlus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/kit";
import { api, errMsg, useFetch, fmtDate, label, splitList } from "../lib/api";
import { PageHeader, btn, TextField, TextArea, SelectField, ErrorSummary, ErrorBox, Loading, EmptyState, StatusBadge, SampleBadge } from "../components/kit";
import { downloadResume } from "./MyStuff";

const PERMS = { owner: ["profile", "jobs", "applicants", "team", "view"], manager: ["profile", "jobs", "applicants", "view"], recruiter: ["jobs", "applicants", "view"], viewer: ["view"] };
const can = (role, perm) => (PERMS[role] || []).includes(perm);
const ROLE_LABEL = { owner: "Owner", manager: "Manager", recruiter: "Recruiter", viewer: "Viewer" };
const roleLabel = (r) => ROLE_LABEL[r] || "Recruiter";
const TEAM_ROLES = [["manager", "Manager — profile, jobs & applicants"], ["recruiter", "Recruiter — jobs & applicants"], ["viewer", "Viewer — read-only"]];

function ReadOnlyCompany({ company }) {
  const rows = [["Website", company.website], ["Headquarters", company.location], ["Company size", company.size], ["Sector", company.sector], ["About", company.about], ["Accessibility policy", company.accessibility_policy]].filter(([, v]) => v);
  return (
    <div data-testid="company-readonly">
      <p className="text-sm text-sub">Only the owner or a manager can edit the company profile.</p>
      <dl className="mt-3 space-y-3">{rows.map(([k, v]) => <div key={k}><dt className="text-sm font-semibold text-sub">{k}</dt><dd className="whitespace-pre-line">{v}</dd></div>)}</dl>
    </div>
  );
}

function CompanyForm({ company, onSaved }) {
  const [f, setF] = useState({ name: "", website: "", about: "", location: "", size: "", sector: "", accessibility_policy: "", ...(company || {}) });
  const [err, setErr] = useState("");
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 2) { setErr("Enter your company name."); return; }
    if (f.website && !/^https?:\/\//.test(f.website)) { setErr("Website must start with http:// or https://"); return; }
    try { const { data } = await api.put("/employer/company", f); setErr(""); toast.success("Company profile saved"); onSaved(data); } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <form onSubmit={save} noValidate className="space-y-4" data-testid="company-form">
      <TextField id="co-name" label="Company name" required value={f.name} onChange={on("name")} />
      <div className="grid gap-4 sm:grid-cols-2"><TextField id="co-website" label="Website" type="url" value={f.website} onChange={on("website")} /><TextField id="co-location" label="Headquarters" value={f.location} onChange={on("location")} /></div>
      <div className="grid gap-4 sm:grid-cols-2"><SelectField id="co-size" label="Company size" value={f.size} onChange={on("size")} placeholder="Not specified" options={["1-10", "11-50", "51-200", "201-1000", "1000+"]} /><TextField id="co-sector" label="Sector" value={f.sector} onChange={on("sector")} /></div>
      <TextArea id="co-about" label="About the company" rows={4} value={f.about} onChange={on("about")} />
      <TextArea
        id="co-a11y"
        label="Workplace accessibility policy"
        hint="Describe only what genuinely exists. OpenPath does not verify these claims."
        rows={3}
        value={f.accessibility_policy}
        onChange={on("accessibility_policy")}
      />
      {err && <ErrorBox message={err} />}
      <button className={btn()} data-testid="company-save-btn">Save company profile</button>
    </form>
  );
}

const ACTIVITY_LABELS = { posted_job: "posted a job", applicant_under_review: "moved an applicant to under review", applicant_interview: "moved an applicant to interview", applicant_offered: "made an offer", applicant_rejected: "rejected an applicant" };
const activityLabel = (a) => ACTIVITY_LABELS[a] || a;

function ActivityLog() {
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const qs = new URLSearchParams({ ...(actor && { actor_id: actor }), ...(action && { action }) }).toString();
  const r = useFetch(`/employer/activity${qs ? `?${qs}` : ""}`);
  if (!r.data) return null;
  const { items = [], actors = [], actions = [] } = r.data;
  if (!actors.length) return null;
  const sel = "min-h-[44px] rounded-lg border border-field bg-white px-2 text-sm";
  const exportCsv = async () => {
    try {
      const { data } = await api.get("/employer/activity/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: "team-activity.csv" });
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error("Could not export the activity log."); }
  };
  return (
    <div className="mt-6 border-t border-line pt-4" data-testid="team-activity">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-bold">Recent team activity</h3>
        <button className={btn("secondary", "sm")} onClick={exportCsv} data-testid="activity-export-btn"><Download className="h-4 w-4" aria-hidden="true" />Export CSV</button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <select aria-label="Filter activity by person" value={actor} onChange={(e) => setActor(e.target.value)} className={sel} data-testid="activity-filter-actor">
          <option value="">All people</option>
          {actors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select aria-label="Filter activity by action" value={action} onChange={(e) => setAction(e.target.value)} className={sel} data-testid="activity-filter-action">
          <option value="">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{activityLabel(a)}</option>)}
        </select>
      </div>
      <ul className="mt-3 space-y-1 text-sm text-sub" aria-live="polite">
        {items.length ? items.map((a) => <li key={a.id} data-testid={`activity-${a.id}`}><span className="font-semibold text-ink">{a.actor_name}</span> {activityLabel(a.action)}{a.detail && ` · ${a.detail}`} · {fmtDate(a.created_at)}</li>) : <li data-testid="activity-empty">No activity matches this filter.</li>}
      </ul>
    </div>
  );
}

function TeamPanel() {
  const { user } = useAuth();
  const nav = useNavigate();
  const r = useFetch("/employer/team");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("recruiter");
  const [err, setErr] = useState("");
  const invite = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) { setErr("Enter a valid email address, like name@example.com."); return; }
    try { const { data } = await api.post("/employer/team/invite", { email, member_role: role }); setEmail(""); setErr(""); toast.success(data.email_sent ? "Invitation sent by email and in-app" : "Invitation created. Email delivery is unavailable, so ask them to sign in with this email to accept."); r.reload(); }
    catch (e2) { setErr(errMsg(e2)); }
  };
  const remove = async (m, leaving) => {
    if (!window.confirm(leaving ? `Leave ${r.data.company.name}? You will lose access to its listings and applicants.` : `Remove ${m.name || m.email} from the team?`)) return;
    try { await api.delete(`/employer/team/${m.id}`); toast.success(leaving ? "You left the company" : "Removed"); if (leaving) nav("/dashboard"); else r.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const changeRole = async (m, member_role) => {
    try { await api.patch(`/employer/team/${m.id}/role`, { member_role }); toast.success("Access level updated"); r.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (r.loading) return <Loading />;
  if (r.error) return <ErrorBox message={r.error} />;
  const { owner, members, my_role } = r.data;
  const isOwner = my_role === "owner";
  const mine = members.find((m) => m.user_id === user.id && m.status === "active");
  return (
    <section className="card p-5 mt-6" aria-labelledby="team-h" data-testid="team-panel">
      <h2 id="team-h" className="flex items-center gap-2 text-xl font-bold"><Users className="h-5 w-5" aria-hidden="true" />Team</h2>
      <p className="mt-1 text-sm text-sub"><strong>Managers</strong> can edit the company profile, post jobs and manage applicants. <strong>Recruiters</strong> can post jobs and manage applicants. <strong>Viewers</strong> have read-only access. Only the owner can invite, remove or change access levels.</p>
      <ul className="mt-4 divide-y divide-line" data-testid="team-members-list">
        <li className="py-3 flex flex-wrap items-center justify-between gap-2"><div><p className="font-semibold">{owner?.name}</p><p className="text-sm text-sub">{owner?.email}</p></div><Badge tone="green">Owner</Badge></li>
        {members.map((m) => (
          <li key={m.id} className="py-3 flex flex-wrap items-center justify-between gap-2" data-testid={`team-member-${m.id}`}>
            <div><p className="font-semibold">{m.name || m.email}</p><p className="text-sm text-sub">{m.email}{m.status === "invited" && ` · invited by ${m.invited_by_name}`}</p></div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={m.status === "active" ? "blue" : "amber"} testid={`member-role-badge-${m.id}`}>{m.status === "active" ? roleLabel(m.member_role) : "Invitation pending"}</Badge>
              {isOwner && m.status === "active" && (
                <select aria-label={`Change access level for ${m.email}`} value={m.member_role || "recruiter"} onChange={(e) => changeRole(m, e.target.value)} className="min-h-[44px] rounded-lg border border-field bg-white px-2 text-sm" data-testid={`member-role-select-${m.id}`}>
                  {TEAM_ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              )}
              {isOwner && <button className={btn("ghost", "sm")} onClick={() => remove(m)} data-testid={`remove-member-${m.id}`}>{m.status === "invited" ? "Cancel invite" : "Remove"}<span className="sr-only"> {m.email}</span></button>}
            </div>
          </li>
        ))}
      </ul>
      {isOwner ? (
        <form onSubmit={invite} noValidate className="mt-4 space-y-3" data-testid="team-invite-form">
          <TextField
            id="team-invite-email"
            label="Invite a colleague by email"
            type="email"
            autoComplete="off"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={err}
            hint="They accept from their OpenPath dashboard after signing in with this email."
          />
          <SelectField id="team-invite-role" label="Access level" value={role} onChange={(e) => setRole(e.target.value)} options={TEAM_ROLES} hint="You can change this at any time." />
          <button className={btn("primary", "sm")} data-testid="team-invite-submit"><UserPlus className="h-4 w-4" aria-hidden="true" />Send invitation</button>
        </form>
      ) : mine && <button className={`${btn("danger", "sm")} mt-4`} onClick={() => remove(mine, true)} data-testid="leave-company-btn">Leave this company</button>}
      {isOwner && <ActivityLog />}
    </section>
  );
}

export function EmployerHome() {
  const co = useFetch("/employer/company");
  const jobs = useFetch("/employer/jobs");
  const setStatus = async (j, status) => {
    if (!window.confirm(`${label(status === "closed" ? "close" : "archive")} “${j.title}”? It will no longer accept applications.`)) return;
    try { await api.post(`/jobs/${j.id}/status`, { status }); toast.success(`Listing ${status}`); jobs.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (co.loading) return <div className="container-x"><Loading /></div>;
  return (
    <>
      <PageHeader eyebrow="Employer hub" title={co.data?.name || "Set up your company"} description="Manage your company profile, job listings and applicants. New and edited listings are reviewed by moderators before publishing.">
        {co.data && can(co.data.my_role, "jobs") && <Link to="/employer/jobs/new" className={btn()} data-testid="new-job-btn"><Plus className="h-5 w-5" aria-hidden="true" />Post a job</Link>}
      </PageHeader>
      <div className="container-x py-8 grid gap-8 lg:grid-cols-[1fr_420px]">
        <section aria-labelledby="jobs-h">
          <h2 id="jobs-h" className="text-2xl font-bold">Your job listings</h2>
          <div className="mt-4">
            {!co.data ? <EmptyState icon={Briefcase} title="Create your company profile first" body="You'll be able to post jobs once your company profile is saved." /> : jobs.loading ? <Loading /> : jobs.error ? <ErrorBox message={jobs.error} /> : jobs.data.length ? (
              <ul className="space-y-4" data-testid="employer-jobs-list">
                {jobs.data.map((j) => (
                  <li key={j.id} className="card p-5" data-testid={`employer-job-${j.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2"><div><h3 className="text-lg font-bold">{j.title}</h3><p className="text-sm text-sub">Created {fmtDate(j.created_at)} · {j.applicant_count} applicants</p></div><div className="flex flex-wrap gap-2">{j.is_sample && <SampleBadge />}<StatusBadge status={j.status} /></div></div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link to={`/employer/jobs/${j.id}/applicants`} className={btn("primary", "sm")} data-testid={`view-applicants-${j.id}`}><Users className="h-4 w-4" aria-hidden="true" />Applicants</Link>
                      {can(co.data?.my_role, "jobs") && <Link to={`/employer/jobs/${j.id}/edit`} className={btn("secondary", "sm")} data-testid={`edit-job-${j.id}`}>Edit</Link>}
                      <Link to={`/jobs/${j.id}`} className={btn("ghost", "sm")}>Preview</Link>
                      {can(co.data?.my_role, "jobs") && !["closed", "archived"].includes(j.status) && <button className={btn("secondary", "sm")} onClick={() => setStatus(j, "closed")} data-testid={`close-job-${j.id}`}>Close</button>}
                      {can(co.data?.my_role, "jobs") && j.status !== "archived" && <button className={btn("ghost", "sm")} onClick={() => setStatus(j, "archived")} data-testid={`archive-job-${j.id}`}>Archive</button>}
                    </div>
                  </li>
                ))}
              </ul>
            ) : <EmptyState icon={Briefcase} title="No listings yet" body="Post your first job to start receiving applications." action={<Link to="/employer/jobs/new" className={btn()}>Post a job</Link>} />}
          </div>
        </section>
        <section className="self-start" aria-labelledby="co-h">
          <div className="card p-5"><h2 id="co-h" className="text-xl font-bold mb-1">Company profile</h2>{co.data?.my_role && <p className="mb-4 text-sm text-sub" data-testid="my-company-role">Your role: {roleLabel(co.data.my_role)}</p>}{(!co.data || can(co.data.my_role, "profile")) ? <CompanyForm company={co.data} onSaved={(c) => co.setData(c)} /> : <ReadOnlyCompany company={co.data} />}</div>
          {co.data && <TeamPanel />}
        </section>
      </div>
    </>
  );
}

const EMPTY_JOB = { title: "", description: "", responsibilities: "", required_qualifications: "", preferred_qualifications: "", skills: "", location: "", work_mode: "", experience_level: "", employment_type: "", salary_min: "", salary_max: "", sector: "", deadline: "", recruitment_process: "", accessibility_info: "", accommodation_policy: "" };

export function JobForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const meta = useFetch("/meta");
  const [f, setF] = useState(EMPTY_JOB);
  const [errors, setErrors] = useState({});
  const [serverErr, setServerErr] = useState("");
  useEffect(() => {
    if (id) api.get(`/jobs/${id}`).then(({ data }) => setF({ ...EMPTY_JOB, ...data.job, skills: (data.job.skills || []).join(", "), salary_min: data.job.salary_min ?? "", salary_max: data.job.salary_max ?? "" })).catch((e) => setServerErr(errMsg(e)));
  }, [id]);
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    const er = {};
    if (f.title.trim().length < 3) er["job-title"] = "Enter a job title.";
    if (f.description.trim().length < 20) er["job-description"] = "Write a description of at least 20 characters.";
    if (!f.work_mode) er["job-work-mode"] = "Choose a work arrangement.";
    if (!f.experience_level) er["job-experience"] = "Choose an experience level.";
    if (!f.employment_type) er["job-type"] = "Choose an employment type.";
    if (f.salary_min && f.salary_max && Number(f.salary_min) > Number(f.salary_max)) er["job-salary-max"] = "Maximum salary must be greater than the minimum.";
    setErrors(er); setServerErr("");
    if (Object.keys(er).length) return;
    const body = { ...f, skills: splitList(f.skills), salary_min: f.salary_min ? Number(f.salary_min) : null, salary_max: f.salary_max ? Number(f.salary_max) : null };
    ["id", "company_id", "company_name", "owner_id", "status", "featured", "is_sample", "created_at", "published_at", "salary_currency"].forEach((k) => delete body[k]);
    try { await (id ? api.put(`/jobs/${id}`, body) : api.post("/jobs", body)); toast.success("Listing submitted for review"); nav("/employer"); } catch (e2) { setServerErr(errMsg(e2)); }
  };
  const num = (k) => (e) => setF({ ...f, [k]: e.target.value.replace(/\D/g, "") });
  return (
    <>
      <PageHeader eyebrow="Employer hub" title={id ? "Edit job listing" : "Post a job"} description="Listings are reviewed by moderators before they are published. Edits to a live listing send it back for review." />
      <form onSubmit={save} noValidate className="container-x py-8 max-w-3xl space-y-5" data-testid="job-form">
        <ErrorSummary errors={errors} />
        <TextField id="job-title" label="Job title" required value={f.title} onChange={on("title")} error={errors["job-title"]} />
        <TextArea id="job-description" label="Job description" required rows={6} value={f.description} onChange={on("description")} error={errors["job-description"]} />
        <TextArea id="job-responsibilities" label="Responsibilities" rows={4} value={f.responsibilities} onChange={on("responsibilities")} />
        <TextArea id="job-required" label="Required qualifications" rows={3} value={f.required_qualifications} onChange={on("required_qualifications")} />
        <TextArea id="job-preferred" label="Preferred qualifications" rows={3} value={f.preferred_qualifications} onChange={on("preferred_qualifications")} />
        <TextField id="job-skills" label="Skills" hint="Separate with commas" value={f.skills} onChange={on("skills")} />
        <div className="grid gap-5 sm:grid-cols-3">
          <SelectField id="job-work-mode" label="Work arrangement" required value={f.work_mode} onChange={on("work_mode")} placeholder="Choose" options={[["remote", "Remote"], ["hybrid", "Hybrid"], ["onsite", "On-site"]]} error={errors["job-work-mode"]} />
          <SelectField id="job-experience" label="Experience level" required value={f.experience_level} onChange={on("experience_level")} placeholder="Choose" options={[["entry", "Entry"], ["mid", "Mid"], ["senior", "Senior"]]} error={errors["job-experience"]} />
          <SelectField id="job-type" label="Employment type" required value={f.employment_type} onChange={on("employment_type")} placeholder="Choose" options={[["full_time", "Full-time"], ["part_time", "Part-time"], ["contract", "Contract"], ["internship", "Internship"]]} error={errors["job-type"]} />
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField id="job-location" label="Location" value={f.location} onChange={on("location")} />
          <SelectField id="job-sector" label="Sector" value={f.sector} onChange={on("sector")} placeholder="Choose" options={meta.data?.job_sectors || []} />
          <TextField id="job-salary-min" label="Minimum salary (INR per year)" inputMode="numeric" value={f.salary_min} onChange={num("salary_min")} />
          <TextField id="job-salary-max" label="Maximum salary (INR per year)" inputMode="numeric" value={f.salary_max} onChange={num("salary_max")} error={errors["job-salary-max"]} />
          <TextField id="job-deadline" label="Application deadline" type="date" value={f.deadline} onChange={on("deadline")} />
        </div>
        <TextArea id="job-process" label="Recruitment process" rows={3} value={f.recruitment_process} onChange={on("recruitment_process")} />
        <fieldset className="card p-5 space-y-4"><legend className="px-2 font-bold text-navy">Workplace accessibility (optional)</legend>
          <p className="text-sm text-sub">Only describe features and processes that genuinely exist. Candidates will see this is provided by you.</p>
          <TextArea id="job-a11y" label="Workplace accessibility information" rows={3} value={f.accessibility_info} onChange={on("accessibility_info")} />
          <TextArea id="job-accommodation" label="Accommodation policy" rows={3} value={f.accommodation_policy} onChange={on("accommodation_policy")} />
        </fieldset>
        {serverErr && <ErrorBox message={serverErr} />}
        <div className="flex gap-3"><button className={btn()} data-testid="job-submit-btn">{id ? "Save and resubmit" : "Submit for review"}</button><Link to="/employer" className={btn("secondary")}>Cancel</Link></div>
      </form>
    </>
  );
}

export function Applicants() {
  const { id } = useParams();
  const r = useFetch(`/jobs/${id}/applicants`);
  const update = async (a, status) => {
    try { await api.put(`/applications/${a.id}/status`, { status }); toast.success(`Status updated to ${label(status)}`); r.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (r.loading) return <div className="container-x"><Loading /></div>;
  if (r.error) return <div className="container-x py-10"><ErrorBox message={r.error} /></div>;
  const { job, applications } = r.data;
  const canStatus = r.data.can_status;
  return (
    <>
      <PageHeader eyebrow="Applicants" title={job.title} description={`${applications.length} application(s). Candidate information is visible only to you and administrators.`}><Link to="/employer" className={btn("secondary")}>Back to employer hub</Link></PageHeader>
      <div className="container-x py-8">
        {applications.length ? <ul className="space-y-4" data-testid="applicants-list">{applications.map((a) => (
          <li key={a.id} className="card p-5" data-testid={`applicant-${a.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div><h2 className="text-xl font-bold">{a.applicant_name}</h2><p className="text-sub">{a.candidate?.headline}{a.candidate?.location && ` · ${a.candidate.location}`}</p><p className="text-sm text-sub">{a.applicant_email} · Applied {fmtDate(a.created_at)}</p></div>
              <StatusBadge status={a.status} />
            </div>
            {a.candidate?.skills?.length > 0 && <p className="mt-2"><strong>Skills:</strong> {a.candidate.skills.join(", ")}</p>}
            {a.candidate?.experience && <p className="mt-1 whitespace-pre-line"><strong>Experience:</strong> {a.candidate.experience}</p>}
            {a.cover_letter && <details className="mt-2"><summary className="cursor-pointer min-h-[44px] flex items-center font-semibold text-link">Cover letter</summary><p className="whitespace-pre-line">{a.cover_letter}</p></details>}
            {a.accommodation_request && <p className="mt-2 rounded-lg bg-surface p-3"><strong>Accommodation request (confidential): </strong>{a.accommodation_request}</p>}
            <div className="mt-4 flex flex-wrap items-end gap-3">
              {a.resume_name && <button className={btn("secondary", "sm")} onClick={() => downloadResume(a.id, a.resume_name)} data-testid={`download-resume-${a.id}`}><Download className="h-4 w-4" aria-hidden="true" />Download CV</button>}
              {a.status !== "withdrawn" && canStatus && <div className="w-56"><SelectField id={`status-${a.id}`} label="Update status" value="" onChange={(e) => e.target.value && update(a, e.target.value)} placeholder="Choose new status" options={[["under_review", "Under review"], ["interview", "Interview"], ["offered", "Offered"], ["rejected", "Rejected"]]} /></div>}
            </div>
          </li>
        ))}</ul> : <EmptyState icon={Users} title="No applicants yet" body="Applications will appear here as candidates apply." />}
      </div>
    </>
  );
}
